import { Button, CircularProgress, TextField, WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { CancelOutlined } from '@material-ui/icons';
import { Autocomplete } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import Select from 'react-select';
import { CustomerContractStatusOptions } from '../../codec/customerContractInDTODecoder';
import { ICustomerContractSearchDTO } from '../../repository/jago/model/input/ICustomerContractSearchDTO';
import { ICustomerOptionDTO } from '../../repository/jago/model/input/ICustomerOptionDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import translations from '../../translations/i18next';
import Permission from '../Permission/Permission';
import ReduxLanguage from '../ReduxLanguage';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    padding: 10,
  },
  logo: {
    margin: 5,
    marginBottom: 15,
    alignSelf: 'center',
    height: 45,
  },
  text: {
    margin: 0,
    marginBottom: 0,
  },
  innerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 10,
    marginLeft: 10,
  },
  title: {
    color: 'white',
  },
  circularProgress: {
    color: 'white',
  },
});


export interface ICustomerContractsSearchBarProps extends WithStyles<typeof styles> {
  onSearch: (searchValues: ICustomerContractSearchDTO) => void;
  onResetSearch: () => void;
  onCreateNewEntityButtonClick: () => void;
  hideNewButton?: boolean;
  onFilterCustomers: (value: string) => void;
  customersOptions: ICustomerOptionDTO[];
}

export interface ICustomerContractStatusOption {
  label: string;
  value: string;
  original: string;
}

export interface ICustomerContractsSearchBarState {
  searchValues: Record<keyof ICustomerContractSearchDTO, string>;
  statusSelected: ICustomerContractStatusOption | undefined;
  customerSelected: ICustomerOptionDTO | undefined;
  searchText: string;
}

class CustomerContractsSearchBar extends React.Component<ICustomerContractsSearchBarProps, ICustomerContractsSearchBarState> {
  constructor(props: ICustomerContractsSearchBarProps) {
    super(props);
    this.state = {
      searchValues: {
        customerId: '',
        status: undefined,
        search: '',
      },
      statusSelected: undefined,
      customerSelected: undefined,
      searchText: '',
    }
  }

  public onSetSearchValue(field: keyof ICustomerContractSearchDTO, value: string) {
    const { searchValues } = this.state;
    const { onSearch } = this.props;
    searchValues[field] = value ? value.toString() : '';
    this.setState({ searchValues });
    onSearch(searchValues);
  }

  public onSearch() {
    const { onSearch } = this.props;
    onSearch(this.state.searchValues);
  }

  public async onSelectStatus(statusOption?: ICustomerContractStatusOption) {
    const selectedValue = statusOption ? statusOption.original : undefined;
    this.onSetSearchValue('status', selectedValue || undefined);
    this.setState({ statusSelected: statusOption });
    setTimeout(() => this.onSearch(), 200);
  }

  public onResetFields() {
    const { onResetSearch } = this.props;

    this.setState({ 
      searchValues: { customerId: undefined, status: undefined, search: '' }, 
      statusSelected: undefined,
      customerSelected: undefined,
      searchText: '',
    }, () => {
      onResetSearch();
    });
  }

  public onSelectCustomer(customer: ICustomerOptionDTO) {
    if (customer && customer.value) {
      this.onSetSearchValue('customerId', customer.value.id.toString());
      this.setState({ customerSelected: customer });
    } else {
      this.onSetSearchValue('customerId', undefined);
      this.setState({ customerSelected: undefined, searchText: '' });
    }
    setTimeout(() => this.onSearch(), 200);
  }

  public render() {
    const { classes, onCreateNewEntityButtonClick, hideNewButton, customersOptions } = this.props;
    const { searchValues, statusSelected, customerSelected, searchText } = this.state;
    const debouncedSearch = _.debounce((text) => this.props.onFilterCustomers(text), 500);

    const statusOptionsFormatted: ICustomerContractStatusOption[] = Object.keys(CustomerContractStatusOptions).map(key => ({
      label: translations.t(`credits.customerContractStatus_${key}`),
      value: key,
      original: key,
    }));

    return (
      <div className={classes.container}>
        <div style={{ marginLeft: 0, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <TextField
            id="outlined-basic"
            label={<ReduxLanguage languageKey={'credits.customerContractName'} />}
            variant="outlined"
            type="search"
            style={{ marginRight: 10, minWidth: 200 }}
            value={searchValues.search}
            onChange={e => this.onSetSearchValue('search', e.target.value)}
          />
          {!hideNewButton ? (
            <Autocomplete
              options={customersOptions}
              style={{ minWidth: 250 }}
              getOptionLabel={(option) => option.label}
              inputValue={customerSelected ? customerSelected.label : searchText}
              value={customerSelected}
              placeholder={<ReduxLanguage languageKey={'forms.selectCustomer'} />}
              isClearable
              autoComplete
              onChange={(e, value) => {this.onSelectCustomer(value)}}
              onInputChange={(event, newInputValue) => { 
                this.setState({ searchText: newInputValue }); 
                debouncedSearch(newInputValue); 
              }}
              renderInput={(params) => <TextField {...params} variant="outlined" style={{ width: 250 }} label={<ReduxLanguage languageKey="forms.customerName" />}/>}
            />
          ) : null}
          <Select
            isClearable
            key={`my_unique_select_key__${searchValues.status}`}
            value={statusSelected}
            options={statusOptionsFormatted}
            placeholder={<ReduxLanguage languageKey={'recharges.statusSelection'} />}
            onChange={(e) => {this.onSelectStatus(e)}}
            styles={{
              menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
              control: (styles) => ({ ...styles, minHeight: 55, minWidth: 200, marginLeft: 10 }),
            }} 
          />
          <Button
            variant="contained"
            style={{ backgroundColor: 'red', color: 'white', marginLeft: 10, fontWeight: 'bold'  }}
            onClick={() => this.onResetFields()}
          >
            <CancelOutlined style={{ color: 'white' }} />
          </Button>
          {!hideNewButton ? (
            <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.CONTRACTS_CREATE}>
              <Button
                variant="contained"
                style={{ backgroundColor: '#5AC0B1', color: 'white', width: '100%',  height: 40, marginLeft: 40, minWidth: 100, fontSize: 13, fontWeight: 'bold'  }}
                onClick={() => onCreateNewEntityButtonClick()}
              >
                <ReduxLanguage languageKey="forms.createNewCustomer" />
              </Button>
            </Permission>
          ) : null}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(CustomerContractsSearchBar);
